import React from 'react';
import { BLUE_RAMP, formatUsdFull } from './common';
import { cn } from '../../../../utils';

interface CompositionBarChartProps {
  data: Array<{ name: string; value: number }>;
  /** Rendered above the bar as the sum being broken down. */
  totalLabel?: string;
  valueFormatter?: (value: number) => string;
  /** Overrides the ramp — used when a slice carries risk semantics. */
  colors?: readonly string[];
  /** Rendered under the legend, for a related figure that is not part of the total. */
  footer?: React.ReactNode;
}

/**
 * A single 100%-width stacked bar plus a legend table — the clearest way to
 * answer "what is this total made of" when there are only a handful of parts
 * and the parts have very different magnitudes. A donut hides a 2% slice; a
 * stacked bar with a printed share does not.
 *
 * Plain SVG/CSS rather than recharts: at one bar the library adds weight
 * without adding anything a business reader can use.
 */
const CompositionBarChart: React.FC<CompositionBarChartProps> = ({
  data,
  totalLabel,
  valueFormatter = formatUsdFull,
  colors = BLUE_RAMP,
  footer,
}) => {
  const total = data.reduce((sum, part) => sum + part.value, 0);
  const share = (value: number) => (total > 0 ? (value / total) * 100 : 0);

  return (
    <div className="flex h-full flex-col justify-center gap-4">
      <div>
        {totalLabel && (
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-blue-900/70">
            {totalLabel}
          </p>
        )}
        <p className="text-2xl font-semibold tabular-nums tracking-tight text-blue-950">
          {valueFormatter(total)}
        </p>
      </div>

      <div
        className="flex h-4 w-full overflow-hidden rounded-full bg-blue-50"
        role="img"
        aria-label={`Composition of ${valueFormatter(total)}`}
      >
        {data.map((part, index) => (
          <div
            key={part.name}
            className={cn('h-full', index === 0 && 'rounded-l-full')}
            style={{
              width: `${share(part.value)}%`,
              backgroundColor: colors[Math.min(index, colors.length - 1)],
            }}
            title={`${part.name}: ${valueFormatter(part.value)}`}
          />
        ))}
      </div>

      <ul className="space-y-1.5">
        {data.map((part, index) => (
          <li key={part.name} className="flex items-center gap-2 text-xs">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: colors[Math.min(index, colors.length - 1)] }}
            />
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{part.name}</span>
            <span className="shrink-0 tabular-nums font-medium text-blue-950">
              {valueFormatter(part.value)}
            </span>
            <span className="w-11 shrink-0 text-right tabular-nums text-muted-foreground">
              {share(part.value).toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>

      {footer != null && <div className="border-t border-blue-100 pt-2 text-xs">{footer}</div>}
    </div>
  );
};

export default CompositionBarChart;
